import { defineStore } from "pinia";
import { computed, ref } from "vue";

const STORAGE_KEY = "web1-crm.brandSwitcher.selectedCode";

/**
 * Topbar brand-switcher state. When the user picks a brand (e.g.
 * "MAIL2"), every authFetch call adds `X-Brand-Code: MAIL2` so the
 * API's BrandContextMiddleware scopes queries to that brand. Pick
 * "Kaikki brändit" (null) to go back to admin / cross-brand.
 *
 * Persisted in localStorage so the choice survives page reloads.
 *
 * The write is intentionally synchronous inside `select()` (no
 * Vue `watch` indirection): the BrandSwitcher component calls
 * `window.location.reload()` immediately after `select()` returns,
 * which would otherwise fire before Vue's reactivity flushes the
 * watcher → localStorage stays stale → the reload picks up the
 * previous brand. Doing the write here makes the call ordering
 * sync-write → sync-reload, so the new brand survives the reload.
 */
export const useBrandSwitcherStore = defineStore("brandSwitcher", () => {
  const initial = readPersisted();
  const selectedCode = ref<string | null>(initial);

  const isAllBrands = computed(() => selectedCode.value === null);

  function select(code: string | null): void {
    selectedCode.value = code;
    if (code === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, code);
    }
  }

  function reset(): void {
    select(null);
  }

  return { selectedCode, isAllBrands, select, reset };
});

function readPersisted(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}
